/**
 * Buy Together / Compre Junto — Web Component
 * Handles automatic product fetching, price calculations, and add-to-cart.
 */
class BuyTogether extends HTMLElement {
  constructor() {
    super();
    this.mode = this.dataset.mode;
    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId;
    this.productPrice = parseInt(this.dataset.productPrice, 10);
    this.productCompare = parseInt(this.dataset.productCompare, 10);
    this.productVendor = this.dataset.productVendor;
    this.productType = this.dataset.productType;
    this.productHandle = this.dataset.productHandle;
    this.maxSuggestions = parseInt(this.dataset.maxSuggestions, 10) || 2;
    this.extraDiscount = parseInt(this.dataset.extraDiscount, 10) || 0;
    this.discountMinProducts = parseInt(this.dataset.discountMinProducts, 10) || 2;
    this.filterSameBrand = this.dataset.filterSameBrand === 'true';
    this.filterSameType = this.dataset.filterSameType === 'true';
    this.useRecommendations = this.dataset.useRecommendations === 'true';
    this.rootUrl = (window.routes && window.routes.root_url) ? window.routes.root_url.replace(/\/$/, '') : '';
    this.cacheKey = [
      'buy-together',
      this.productId,
      this.maxSuggestions,
      this.productVendor,
      this.productType,
      this.filterSameBrand,
      this.filterSameType,
      this.useRecommendations
    ].join(':');

    this.totalEl = this.querySelector('[data-buy-together-total]');
    this.priceBeforeEl = this.querySelector('[data-buy-together-price-before]');
    this.pvprEl = this.querySelector('[data-buy-together-pvpr]');
    this.savingsEl = this.querySelector('[data-buy-together-savings]');
    this.addBtn = this.querySelector('[data-buy-together-add]');
    this.discountNote = this.querySelector('[data-buy-together-discount-note]');
    this.discountRow = this.querySelector('[data-buy-together-discount-row]');
    this.discountValue = this.querySelector('[data-buy-together-discount-value]');
    this.autoContainer = this.querySelector('[data-buy-together-auto]');
    this.productsWrap = this.querySelector('.buy-together__products');
  }

  connectedCallback() {
    if (this.mode === 'auto') {
      this.fetchAutoProducts();
    } else {
      this.bindCheckboxes();
      this.recalculate();
      this.addBtn.disabled = false;
    }
    this.addBtn.addEventListener('click', () => this.addAllToCart());
  }

  async fetchAutoProducts() {
    try {
      const cachedProducts = this.getCachedProducts();
      if (cachedProducts?.length) {
        this.renderAutoProducts(cachedProducts);
        this.bindCheckboxes();
        this.recalculate();
        this.addBtn.disabled = false;
        return;
      }

      const vendor = this.productVendor;
      const type = this.productType;
      const suggestions = [];
      const usedHandles = new Set([this.productHandle]);

      const addSuggestion = (product) => {
        if (!product || suggestions.length >= this.maxSuggestions) return false;
        if (usedHandles.has(product.handle) || product.available === false) return false;
        suggestions.push(product);
        usedHandles.add(product.handle);
        return true;
      };

      const addRecommendations = async (
        limit = Math.max(this.maxSuggestions + 6, 10),
        allowUnfiltered = this.useRecommendations || (!this.filterSameBrand && !this.filterSameType),
        intent = 'complementary'
      ) => {
        const recUrl = `/recommendations/products.json?product_id=${this.productId}&limit=${limit}&intent=${intent}`;
        const recData = await this.fetchJson(recUrl);
        const recProducts = (recData.products || [])
          .filter((p) => !usedHandles.has(p.handle) && p.available !== false);

        if (this.useRecommendations || (!this.filterSameBrand && !this.filterSameType)) {
          recProducts.some((p) => addSuggestion(p) && suggestions.length >= this.maxSuggestions);
          return;
        }

        const prioritized = [];
        const remaining = [];
        recProducts.forEach((p) => {
          const sameBrand = this.filterSameBrand && vendor && p.vendor === vendor;
          const sameType = this.filterSameType && type && p.type === type;
          if (sameBrand || sameType) {
            prioritized.push(p);
          } else {
            remaining.push(p);
          }
        });

        const productsToAdd = allowUnfiltered ? [...prioritized, ...remaining] : prioritized;
        productsToAdd.some((p) => addSuggestion(p) && suggestions.length >= this.maxSuggestions);
      };

      try {
        await addRecommendations();
      } catch (e) { /* fallback below */ }

      // If complementary intent returned nothing, try related intent
      if (suggestions.length < this.maxSuggestions) {
        try {
          await addRecommendations(Math.max(this.maxSuggestions + 6, 10), true, 'related');
        } catch (e) { /* fallback to search */ }
      }

      // Helper: search products and filter with parallel detail fetches.
      const searchAndFilter = async (queryStr, filterFn) => {
        const query = encodeURIComponent(queryStr);
        const url = `/search/suggest.json?q=${query}&resources[type]=product&resources[limit]=10`;
        const data = await this.fetchJson(url);
        const candidates = (data.resources?.results?.products || [])
          .filter(p => !usedHandles.has(p.handle) && p.available)
          .slice(0, Math.max(this.maxSuggestions - suggestions.length + 2, 0));

        const productPromises = candidates.map(p =>
          this.fetchJson(`/products/${p.handle}.js`).catch(() => null)
        );
        const products = await Promise.all(productPromises);

        for (const pData of products) {
          if (suggestions.length >= this.maxSuggestions) break;
          if (!pData) continue;
          if (filterFn && !filterFn(pData)) continue;
          addSuggestion(pData);
        }
      };

      // If recommendations are not enough, complete filtered results through search.
      if (suggestions.length < this.maxSuggestions && this.filterSameBrand && vendor && !this.useRecommendations) {
        await searchAndFilter(vendor, (p) => p.vendor === vendor);
      }

      // Then complete with same product type when enabled.
      if (suggestions.length < this.maxSuggestions && this.filterSameType && type && !this.useRecommendations) {
        await searchAndFilter(type, (p) => p.type === type);
      }

      // Final broader fallback — search by vendor or type even when using recommendations API.
      if (suggestions.length < this.maxSuggestions) {
        if (vendor) {
          try { await searchAndFilter(vendor, null); } catch (e) { /* skip */ }
        }
        if (suggestions.length < this.maxSuggestions && type) {
          try { await searchAndFilter(type, null); } catch (e) { /* skip */ }
        }
      }

      if (suggestions.length === 0) {
        this.style.display = 'none';
        return;
      }

      this.renderAutoProducts(suggestions);
      this.setCachedProducts(suggestions);
      this.bindCheckboxes();
      this.recalculate();
      this.addBtn.disabled = false;
    } catch (err) {
      console.error('BuyTogether: Failed to fetch products', err);
      this.style.display = 'none';
    }
  }

  fetchJson(url) {
    return fetch(url, {
      credentials: 'same-origin'
    }).then((response) => {
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      return response.json();
    });
  }

  getCachedProducts() {
    if (!window.sessionStorage) return null;
    try {
      const cached = JSON.parse(sessionStorage.getItem(this.cacheKey) || 'null');
      if (!cached || Date.now() - cached.time > 10 * 60 * 1000) return null;
      return cached.products;
    } catch (e) {
      return null;
    }
  }

  setCachedProducts(products) {
    if (!window.sessionStorage || !products?.length) return;
    try {
      sessionStorage.setItem(this.cacheKey, JSON.stringify({
        time: Date.now(),
        products
      }));
    } catch (e) { /* storage can be full or unavailable */ }
  }

  renderAutoProducts(products) {
    if (!this.autoContainer) return;

    let html = '';
    products.forEach(p => {
      const variant = p.variants[0];
      const price = variant.price;
      const compare = variant.compare_at_price || price;
      const image = p.featured_image ? p.featured_image : (p.images && p.images[0] ? p.images[0] : '');
      const imageUrl = typeof image === 'string' ? image : (image.src || '');
      const sizedImage = imageUrl ? this.getSizedImageUrl(imageUrl, '120x') : '';

      html += `
        <div class="buy-together__separator">+</div>
        <div class="buy-together__product" data-buy-together-item data-variant-id="${variant.id}" data-price="${price}" data-compare="${compare}">
          <label class="buy-together__checkbox-wrap">
            <input type="checkbox" checked class="buy-together__checkbox" data-buy-together-checkbox>
            <span class="buy-together__check-mark"></span>
          </label>
          <a href="${this.rootUrl}/products/${p.handle}" class="buy-together__product-image-wrap">
            ${sizedImage ? `<img src="${sizedImage}" alt="${this.escapeHtml(p.title)}" width="60" height="60" loading="eager" decoding="async" class="buy-together__product-image">` : ''}
          </a>
          <div class="buy-together__product-info">
            <a href="${this.rootUrl}/products/${p.handle}" class="buy-together__product-name">${this.truncate(p.title, 50)}</a>
            <div class="buy-together__product-prices">
              <span class="buy-together__product-price">${this.formatMoney(price)}</span>
              ${compare > price ? `<span class="buy-together__product-pvpr">PVPR <s>${this.formatMoney(compare)}</s></span>` : ''}
            </div>
          </div>
        </div>
      `;
    });

    this.autoContainer.innerHTML = html;
  }

  bindCheckboxes() {
    this.querySelectorAll('[data-buy-together-checkbox]').forEach(cb => {
      cb.addEventListener('change', () => this.recalculate());
    });
  }

  recalculate() {
    let totalPrice = this.productPrice;
    let totalCompare = this.productCompare;
    let checkedCount = 1; // current product always counted

    this.querySelectorAll('[data-buy-together-item]').forEach(item => {
      const cb = item.querySelector('[data-buy-together-checkbox]');
      if (cb && !cb.checked) return;
      if (item.classList.contains('buy-together__product--current')) return;

      totalPrice += parseInt(item.dataset.price, 10);
      totalCompare += parseInt(item.dataset.compare, 10);
      checkedCount++;
    });

    // Apply extra discount only if checked products >= min required
    let discountAmount = 0;
    let finalPrice = totalPrice;
    const meetsMinProducts = this.extraDiscount > 0 && checkedCount >= this.discountMinProducts;

    if (meetsMinProducts) {
      discountAmount = Math.round(totalPrice * (this.extraDiscount / 100));
      finalPrice = totalPrice - discountAmount;

      // Show discount row
      if (this.discountRow) {
        this.discountRow.style.display = '';
        this.discountValue.textContent = `-${this.formatMoney(discountAmount)} (-${this.extraDiscount}%)`;
      }
      // Show price before discount
      if (this.priceBeforeEl) {
        this.priceBeforeEl.innerHTML = `<s>${this.formatMoney(totalPrice)}</s>`;
        this.priceBeforeEl.parentElement.style.display = '';
      }
    } else {
      // Hide discount row and price-before
      if (this.discountRow) {
        this.discountRow.style.display = 'none';
      }
      if (this.priceBeforeEl) {
        this.priceBeforeEl.parentElement.style.display = 'none';
      }
    }

    // Show final price
    this.totalEl.textContent = this.formatMoney(finalPrice);

    // PVPR and savings
    if (totalCompare > finalPrice) {
      const savings = totalCompare - finalPrice;
      const pct = Math.round((savings / totalCompare) * 100);
      this.pvprEl.innerHTML = `<s>${this.formatMoney(totalCompare)}</s>`;
      this.savingsEl.textContent = `${this.formatMoney(savings)} a menos sobre o PVPR (-${pct}%)`;
      this.pvprEl.parentElement.style.display = '';
      this.savingsEl.parentElement.style.display = '';
    } else {
      this.pvprEl.parentElement.style.display = 'none';
      this.savingsEl.parentElement.style.display = 'none';
    }

    // Button text
    const btnText = this.querySelector('.buy-together__add-btn-text');
    if (checkedCount > 1) {
      btnText.textContent = `Adicionar ${checkedCount} produtos ao carrinho`;
    } else {
      btnText.textContent = 'Adicionar ao carrinho';
    }
  }

  async addAllToCart() {
    const btn = this.addBtn;
    const btnText = btn.querySelector('.buy-together__add-btn-text');
    const btnLoading = btn.querySelector('.buy-together__add-btn-loading');

    btn.disabled = true;
    btnText.style.display = 'none';
    btnLoading.style.display = 'inline-flex';

    const items = [];
    items.push({ id: parseInt(this.variantId, 10), quantity: 1 });

    this.querySelectorAll('[data-buy-together-item]:not(.buy-together__product--current)').forEach(item => {
      const cb = item.querySelector('[data-buy-together-checkbox]');
      if (cb && cb.checked) {
        items.push({ id: parseInt(item.dataset.variantId, 10), quantity: 1 });
      }
    });

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const sectionsToRender = cartDrawer ? cartDrawer.getSectionsToRender().map(s => s.id) : [];

      const body = { items };
      if (sectionsToRender.length > 0) {
        body.sections = sectionsToRender;
        body.sections_url = window.location.pathname;
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) throw new Error('Failed to add to cart');

      const response = await res.json();

      // Remove is-empty class from cart-drawer element (important when transitioning from empty → non-empty)
      if (cartDrawer) {
        cartDrawer.classList.remove('is-empty');
      }

      // Update and open cart drawer
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(response);
      }

      // Publish cart update event for other components
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'buy-together',
          cartData: response,
        });
      }

      // Check cart for automatic discounts
      const discountRes = await fetch('/cart.js');
      const cart = await discountRes.json();
      const totalDiscount = cart.total_discount || 0;
      if (totalDiscount > 0 && this.discountNote) {
        this.discountNote.textContent = `🎉 Desconto automático aplicado: -${this.formatMoney(totalDiscount)}`;
        this.discountNote.style.display = 'block';
      }

      btnText.textContent = '✓ Adicionado!';
      btnText.style.display = 'inline';
      btnLoading.style.display = 'none';
      btn.classList.add('buy-together__add-btn--success');

      setTimeout(() => {
        this.recalculate();
        btn.disabled = false;
        btn.classList.remove('buy-together__add-btn--success');
      }, 2500);
    } catch (err) {
      console.error('BuyTogether: Add to cart failed', err);
      btnText.textContent = 'Erro — tente novamente';
      btnText.style.display = 'inline';
      btnLoading.style.display = 'none';
      btn.disabled = false;
      setTimeout(() => this.recalculate(), 2000);
    }
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return `${amount} €`;
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  truncate(str, len) {
    return str.length > len ? str.substring(0, len) + '…' : str;
  }

  getSizedImageUrl(url, size) {
    if (!url) return '';
    const match = url.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?.*)?$/i);
    if (match) {
      const prefix = url.split(match[0])[0];
      return `${prefix}_${size}.${match[1]}${match[2] || ''}`;
    }
    return url;
  }
}

customElements.define('buy-together', BuyTogether);
